'use client';

import {
  matchesPublicCategoryProductsSeed,
  type PublicCategoryProductsSeed,
} from '@eliadmin/core/src/components/categories/public-category-products-seed';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { useSearchParams } from 'next/navigation';
import { usePaginationCanonical } from '@eliadmin/core/src/hooks/use-pagination-canonical';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CategoryFilterSheet } from './category-filter-sheet';
import { CategorySortToggle } from './category-sort-toggle';
import { CATEGORY_GRID_PRODUCT_IMAGE_SIZES, ProductCard } from '../product/product-card';
import { Pagination } from '../shared/pagination';
import { CategoryProductsSkeleton } from './category-products-skeleton';

export type CategoryProductsListProps = {
  category: Category;
  locale: string;
  initialProducts?: PublicCategoryProductsSeed;
};
export function CategoryProductsList({
  category,
  locale,
  initialProducts,
}: CategoryProductsListProps) {
  const searchParams = useSearchParams();
  usePaginationCanonical(ROUTES.CATEGORY({ slug: category.slug }), searchParams.toString());
  const filters = useCategoryStore(s => s.filter);
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const parsedPage = Number(searchParams.get('page') ?? '1');
  const currentPage = Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const { t } = useTranslations();

  const queryWithoutPage = buildProductsQuery({
    categories: [category.id],
    manufacturers: filters.manufacturers ?? [],
    results: 16,
    order: filters.sort.order,
    orderBy: filters.sort.orderBy,
    attributes: filters.attributes,
    price_from: filters.price_min,
    price_to: filters.price_max,
    availabilities: filters.availabilities,
    suppliers: filters.suppliers,
  });
  const productResetUri = buildProductsRequest({ ...queryWithoutPage, page: undefined }).uri;
  const [settledFilterUri, setSettledFilterUri] = useState<string | null>(null);
  const isFilterResetPending = settledFilterUri !== null && settledFilterUri !== productResetUri;

  const requestUri = buildProductsRequest({ ...queryWithoutPage, page: currentPage }).uri;
  const productsQuery = useInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(initialProducts, {
      locale,
      requestUri,
      authToken,
      page: currentPage,
    })
      ? { pages: [initialProducts!.data], pageParams: [currentPage] }
      : undefined,
    initialDataUpdatedAt: initialProducts?.updatedAt,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    queryKey: [
      'CATEGORY_PRODUCTS',
      'infinite',
      currentPage,
      productResetUri,
      locale,
      me?.customer.id ?? 'guest',
      authToken,
    ],
    queryFn: ({ pageParam }) =>
      fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
    enabled: isInitialized && !isFilterResetPending,
  });

  useEffect(() => {
    // Establish the baseline only after the initial (possibly URL-hydrated) list
    // loads. A bookmarked page must not reset merely because the component mounts.
    if (settledFilterUri === null) {
      if (productsQuery.data) setSettledFilterUri(productResetUri);
      return;
    }
    if (!isFilterResetPending) return;
    if (currentPage !== 1) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      return;
    }
    // Wait for the page reset before enabling a request with the new filters.
    setSettledFilterUri(productResetUri);
  }, [currentPage, isFilterResetPending, productResetUri, productsQuery.data, settledFilterUri]);

  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  if ((!isInitialized && !productsQuery.data) || productsQuery.isPending || !productsQuery.data) {
    return <CategoryProductsSkeleton />;
  }

  const lastPage = productsQuery.data.pages[productsQuery.data.pages.length - 1];
  const nextParams = new URLSearchParams(searchParams.toString());
  nextParams.set('page', String(lastPage.meta.current_page + 1));
  // Build the public route, never an internal ea-bot/ea-query rewrite pathname.
  const nextPageHref = `${ROUTES.CATEGORY({ slug: category.slug })}?${nextParams.toString()}`;
  const previousParams = new URLSearchParams(searchParams.toString());
  if (currentPage > 2) previousParams.set('page', String(currentPage - 1));
  else previousParams.delete('page');
  const previousQuery = previousParams.toString();
  const previousPageHref =
    currentPage > 1
      ? `${ROUTES.CATEGORY({ slug: category.slug })}${previousQuery ? `?${previousQuery}` : ''}`
      : undefined;

  return (
    <section className={cn('custom-section', 'pb-12 sm:pb-16 md:pb-20 lg:pb-24')}>
      <div className={cn('custom-container', 'flex flex-col items-center justify-center gap-6')}>
        <div className="flex w-full flex-col items-center justify-center gap-2 md:flex-row md:justify-between">
          <CategoryFilterSheet />
          <p className="hidden text-sm text-[#636363] sm:text-base md:block">
            {productsQuery.data.pages[0].meta.total} {t('text173')}
          </p>
          <CategorySortToggle />
          <p className="mt-4 block text-sm text-[#636363] sm:text-base md:hidden">
            {productsQuery.data.pages[0].meta.total} {t('text173')}
          </p>
        </div>
        <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-3">
          {flatList.map(product => (
            <ProductCard
              product={product}
              key={product.id}
              imageSizes={CATEGORY_GRID_PRODUCT_IMAGE_SIZES}
            />
          ))}
        </div>
        <Pagination
          query={productsQuery}
          currentLength={flatList.length}
          nextPageHref={nextPageHref}
          previousPageHref={previousPageHref}
        />
      </div>
    </section>
  );
}
